import { client } from '@api/client';

import { IId, TIdName, TNameTitle } from '@interfaces/common/common';
import { IGroupOperationDTO } from '@interfaces/common/defaultDTO';
import { IItemsResponse } from '@interfaces/common/response';
import {
	IChangeUserStatusDTO,
	ICheckEmailAsPoDTO,
	IDeleteUsersDTO,
	IGetUserDTO,
	IGetUsersDTO,
	ISwapUserDTO,
	IUserFormDTO,
} from '@interfaces/user/dto/userDTO';
import {
	IShortUserInfo,
	IUserCheckEmail,
	IUserProfilePO,
} from '@interfaces/user/user';

import { URL } from './urls';

const {
	users,
	usersAsPo,
	roles,
	deleteUsers,
	deleteOneUserAsPo,
	deleteUsersOrganisationLevel,
	deleteUsersAsPoUserTable,
	editUser,
	addUser,
	addUserAsPO,
	userBlock,
	userUnblock,
	currentUser,
	userProfileAsPo,
	usersStatusesAsPo,
	usersOrganizationsAsPo,
	swapUsers,
	organizationsUsersDropdown,
	checkEmail,
	checkEmailAsPo,
	updateUserAsPo,
} = URL;

export const getUsers = ({
	page,
	pageSize,
	searchValue,
	sortDirection,
	sortFieldName,
	isEnabled,
	role,
	organizationId,
}: IGetUsersDTO): Promise<IItemsResponse<IGetUserDTO>> => client.get(`/${users}`, {
	params: {
		page,
		pageSize,
		searchValue,
		role,
		isEnabled,
		sortDirection,
		sortFieldName,
		organizationId,
	},
});

export const getRoles = async (organizationId?: string | null): Promise<TNameTitle[]> => {
	return client.get(roles, {
		params: {
			organizationId,
		},
	});
};

export const getDefaultSwapUsersDropdown = (): Promise<IShortUserInfo[]> => {
	return client.get(organizationsUsersDropdown);
};

export const getSwapUsersDropdownPlatformOwner = (id?: string | null): Promise<IShortUserInfo[]> => {
	return client.get(`${organizationsUsersDropdown}?organizationId=${id}`);
};

export const removeUsersOrganisationLevel = ({
	filter,
	ids,
	isAll,
	organizationId,
}: IGroupOperationDTO<IDeleteUsersDTO | null>): Promise<void> => {
	return client.post(`/${deleteUsersOrganisationLevel}`, {
		filter,
		ids,
		isAll,
		organizationId,
	});
};

export const removeUsersAsPoUserTable = ({
	filter,
	ids,
	isAll,
	organizationId,
}: IGroupOperationDTO<IDeleteUsersDTO | null>): Promise<void> => {
	return client.post(`/${deleteUsersAsPoUserTable}`, {
		filter,
		ids,
		isAll,
		organizationId,
	});
};

export const removeUsers = ({
	filter,
	ids,
	isAll,
	organizationId,
}: IGroupOperationDTO<IDeleteUsersDTO | null>): Promise<void> => {
	return client.post(`/${deleteUsers}`, {
		filter,
		ids,
		isAll,
		organizationId,
	});
};

export const removeOneUserAsPo = ({ id }: IId): Promise<void> => {
	return client.post(`/users/${id}/${deleteOneUserAsPo}`);
};

export const updateUser = ({ id, values, organizationId }: IUserFormDTO): Promise<void> => {
	return client.put(
		`/${editUser}/${id}`,
		values,
		organizationId
			? { params: { organizationId } }
			: {},
	);
};

export const updateUserAsPO = ({ id, values }: IUserFormDTO): Promise<void> => {
	return client.post(`/users/${id}/${updateUserAsPo}`, values);
};

export const createUser = ({ values }: IUserFormDTO): Promise<void> => {
	return client.post(`/${addUser}`, values);
};

export const createUserAsPO = ({ values }: IUserFormDTO): Promise<void> => {
	return client.post(`/${addUserAsPO}`, values);
};

export const blockUser = ({ id, organizationId }: IChangeUserStatusDTO): Promise<void> => {
	return client.get(`/${userBlock}/${id}`, {
		params: {
			organizationId,
		},
	});
};

export const disableUserAsPO = ({ id }: IId): Promise<void> => {
	return client.post(`/users/${id}/disable-as-po`);
};

export const enableUserAsPO = ({ id }: IId): Promise<void> => {
	return client.post(`/users/${id}/enable-as-po`);
};

export const checkUserEmailAsPo = (dto: ICheckEmailAsPoDTO): Promise<IUserCheckEmail> => {
	return client.post(checkEmailAsPo, dto);
};

export const checkUserEmail = (dto: ICheckEmailAsPoDTO): Promise<IUserCheckEmail> => {
	return client.post(checkEmail, dto);
};

export const unblockUser = ({ id, organizationId }: IChangeUserStatusDTO): Promise<void> => {
	return client.get(`/${userUnblock}/${id}`, {
		params: {
			organizationId,
		},
	});
};

export const getCurrentUser = (): Promise<IGetUserDTO> => {
	return client.get(`/${currentUser}`);
};

export const getUserStatusesAsPo = (): Promise<TNameTitle[]> => client.get(`/${usersStatusesAsPo}`);
export const getUserOrganizationsAsPo = (): Promise<TIdName[]> => client.get(`/${usersOrganizationsAsPo}`);

export const getProfileUserAsPo = ({ id }: IId): Promise<IUserProfilePO> => {
	return client.get(`users/${id}/${userProfileAsPo}`);
};

export const swapUsersRequest = (dto: ISwapUserDTO): Promise<void> => client.post(`/${swapUsers}`, dto);

export const getUsersAsPo = (params: IGetUsersDTO): Promise<IItemsResponse<IGetUserDTO>> => {
	return client.get(`/${usersAsPo}`, { params });
};
